import { useEffect } from 'react';

export default function useClickBoundary(
  targetNode: HTMLElement | undefined,
  inside: boolean,
  onClick: (event: MouseEvent) => void,
  record = true
): void {
  useEffect(() => {
    if (!record || !targetNode) return;

    function handleClick(event: MouseEvent) {
      if (!targetNode) return;

      const clickedInside = targetNode.contains(event.target as HTMLElement);
      const triggerInside = clickedInside && inside;
      const triggerOutside = !clickedInside && !inside;
      if (triggerInside || triggerOutside) {
        onClick(event);
      }
    }

    // TODO: There is no garuntee that by the time this is called the target in question hasn't been removed. To
    // avoid this we need to be sure this is the first thing to run, but we can't even do that because sometimes
    // we legitimately need to prevent this event handler from happening (e.g. one popover inside another). The
    // best thing I can think of right now is to tie this to mousedown so it runs before the click event and it is
    // very rare that anything needs to run on mousedown as opposed to click so this should always run first. I feel
    // like I'm going to have to make my own event framework to properly solve this but not right now.
    // Maybe we can build something more like https://github.com/mui/material-ui/blob/master/packages/mui-base/src/ClickAwayListener/ClickAwayListener.tsx
    // They actually have the ability to detect if a click happens with the React tree, not just within the DOM tree
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [targetNode, inside, onClick, record]);
}
